import type React from 'react';
import { type Dispatch, type ReactNode, type SetStateAction, useContext } from 'react';
import styled from 'styled-components';
import { useMcpBridge } from '../../mcp/McpBridgeProvider';
import { GuardianContext } from '../GuardianProvider';

const SidebarContainer = styled.div<{ expanded: boolean; isInIframe: boolean; isSidebarOpen: boolean }>`
  z-index: 3;
  overflow: hidden;
  flex: 0 0 auto;
  width: ${(props) => (props.expanded ? 'var(--sidebar-open-width)' : 'var(--sidebar-closed-width)')};
  color: ${(props) => props.theme.textPrimary};
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${(props) => props.theme.backgroundLevel4};
  backdrop-filter: blur(10px);
  transition: width var(--sidebar-slide);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${(props) =>
    props.isInIframe
      ? /* Embedded: the sidebar is a drawer sliding over the host's content, so it leaves the flow
           and floats above it instead of pushing anything. */
        `
        position: fixed;
        top: var(--header-height);
        left: 0;
        height: calc(100vh - var(--header-height));
        transform: translateX(${props.isSidebarOpen ? '0' : '-100%'});
        transition: transform var(--sidebar-slide);
        `
      : /* In flow, below the navbar — which is out of the flow, so the row it sits in spans the
           whole viewport and the rail has to leave that room itself, top and bottom. */
        `
        margin-top: var(--header-height);
        height: calc(100% - var(--header-height));
        `}
`;

/**
 * Holds the content at its final width — no transition here — while the container above animates
 * and clips it. Sized off the animating width, the content re-wrapped on every frame.
 */
const SidebarContent = styled.div<{ expanded: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: ${(props) => (props.expanded ? 'var(--sidebar-open-width)' : 'var(--sidebar-closed-width)')};
`;

type SidebarShellRenderProps = {
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
};

type SidebarShellProps = {
  /** Full sidebar content (collapsed rail + expanded body); receives the expand state. */
  children: (props: SidebarShellRenderProps) => ReactNode;
};

/**
 * Shared sidebar container: the collapsible rail (+ its iframe drawer behavior) and the
 * expand/collapse state. Content — conversation list, dashboard list, … — is supplied by
 * the caller via a render prop.
 */
export const SidebarShell: React.FC<SidebarShellProps> = ({ children }) => {
  const { isInIframe } = useMcpBridge();
  // Expand state lives in context so switching modes (which remounts the sidebar) keeps it open.
  const { isSidebarOpen, isSidebarExpanded, setIsSidebarExpanded } = useContext(GuardianContext);
  const isExpanded = isInIframe || isSidebarExpanded;

  return (
    <SidebarContainer expanded={isExpanded} isInIframe={isInIframe} isSidebarOpen={isSidebarOpen}>
      <SidebarContent expanded={isExpanded}>
        {children({ isExpanded, setIsExpanded: setIsSidebarExpanded })}
      </SidebarContent>
    </SidebarContainer>
  );
};
